// The agency PURL is the portal link (https:// + the agency's domain). These
// helpers and small components let any view copy it, and show it read-only to
// users who cannot edit the domain.
import { useNotify, useRecordContext } from "react-admin";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

export const linkFor = (domain) => (domain ? `https://${domain}` : "");

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // Clipboard API can be unavailable (permissions, insecure context); fall
    // back to a temporary textarea.
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch (e2) {
      return false;
    }
  }
};

// Copies an agency's portal link. `icon` renders a compact icon button (for the
// list action column); otherwise a labeled button (for the details page).
export const PurlCopyButton = ({ domain, icon = false }) => {
  const notify = useNotify();
  const link = linkFor(domain);
  if (!link) return null;
  const copy = async (e) => {
    // In the list the whole row is clickable (rowClick="edit"); keep a copy
    // click from opening the record.
    e.stopPropagation();
    const ok = await copyText(link);
    notify(ok ? "Link copied" : "Could not copy the link", {
      type: ok ? "success" : "error",
    });
  };
  if (icon) {
    return (
      <Tooltip title="Copy PURL">
        <IconButton size="small" aria-label="Copy PURL" onClick={copy}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <Button size="small" startIcon={<ContentCopyIcon />} onClick={copy}>
      Copy PURL
    </Button>
  );
};

// Opens an agency's portal link in a new tab. The PURL text stays a plain text
// node (easy to select and copy by hand), so this button is how you jump to it.
export const PurlOpenButton = ({ domain, icon = false }) => {
  const link = linkFor(domain);
  if (!link) return null;
  const open = (e) => {
    // The list row is clickable (rowClick="edit"); keep this from opening the
    // record as well.
    e.stopPropagation();
    window.open(link, "_blank", "noopener,noreferrer");
  };
  if (icon) {
    return (
      <Tooltip title="Open PURL">
        <IconButton size="small" aria-label="Open PURL" onClick={open}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <Button size="small" startIcon={<OpenInNewIcon />} onClick={open}>
      Open PURL
    </Button>
  );
};

// Datagrid action-column cell: open and copy buttons for the row's agency.
export const PurlListActions = () => {
  const record = useRecordContext();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <PurlOpenButton domain={record?.domain} icon />
      <PurlCopyButton domain={record?.domain} icon />
    </Box>
  );
};

// Read-only PURL for users who cannot edit the domain: the portal link as a
// plain text node (so it can be selected and copied by hand), plus a button to
// open it in a new tab and a copy button. (Full admins get the editable field.)
export const PurlReadonly = ({ domain }) => {
  const link = linkFor(domain);
  return (
    <Box>
      <Typography variant="caption" color="textSecondary">
        PURL
      </Typography>
      {link ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
            {link}
          </Typography>
          <PurlOpenButton domain={domain} icon />
          <PurlCopyButton domain={domain} icon />
        </Box>
      ) : (
        <Typography variant="body1">No custom domain</Typography>
      )}
    </Box>
  );
};
